import React, { useState } from 'react';
import { useSecretGesture, verifyAdminPassphrase } from './adminUnlock';
import { getIconForSymbol } from './blockchainIcons';

const CHAINS = [
  { symbol: 'BTC', name: 'Bitcoin' },
  { symbol: 'ETH', name: 'Ethereum' },
  { symbol: 'BNB', name: 'Binance' },
  { symbol: 'SOL', name: 'Solana' },
  { symbol: 'USDT', name: 'Tether' },
  { symbol: 'LTC', name: 'Litecoin' },
  { symbol: 'TRX', name: 'Tron' },
];

const formatUsd = (raw) => {
  const n = Number(String(raw).replace(/[^\d.]/g, ''));
  if (!isFinite(n)) return '$0.00';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// found: { symbol, crypto, usd } | null. Local-only display value.
const AdminFoundPill = ({ found, onCommit, onClear }) => {
  const [phase, setPhase] = useState('idle'); // 'idle' | 'prompt' | 'editor'
  const [passphrase, setPassphrase] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  const [symbol, setSymbol] = useState(found?.symbol || 'BTC');
  const [crypto, setCrypto] = useState(found?.crypto || '');
  const [usd, setUsd] = useState(found?.usd || '');

  const gestureHandlers = useSecretGesture(() => {
    setPassphrase('');
    setError('');
    setPhase('prompt');
  }, phase === 'idle');

  const verify = async () => {
    setChecking(true);
    setError('');
    try {
      if (await verifyAdminPassphrase(passphrase)) {
        setSymbol(found?.symbol || 'BTC');
        setCrypto(found?.crypto || '');
        setUsd(found?.usd || '');
        setPhase('editor');
      } else {
        setError('Incorrect');
      }
    } catch (e) {
      setError('Network error');
    } finally {
      setChecking(false);
    }
  };

  const save = () => {
    onCommit({
      symbol,
      crypto: String(crypto).trim(),
      usd: String(usd).replace(/[^\d.]/g, ''),
    });
    setPhase('idle');
  };

  const clear = () => {
    onClear();
    setPhase('idle');
  };

  const closeModal = () => setPhase('idle');

  return (
    <>
      <div className={`wallets-status-pill ${found ? 'wallets-status-pill-found' : ''}`} {...gestureHandlers}>
        {found ? (
          <span className="found-wallet-chip">
            <img src={getIconForSymbol(found.symbol)} alt="" className="found-wallet-icon" draggable="false" />
            <span className="found-wallet-text">
              {found.crypto} {found.symbol} · {formatUsd(found.usd)}
            </span>
          </span>
        ) : (
          'No wallets found yet'
        )}
      </div>

      {phase !== 'idle' && (
        <div className="admin-modal-overlay" onMouseDown={closeModal}>
          <div className="admin-modal" onMouseDown={(e) => e.stopPropagation()}>
            {phase === 'prompt' ? (
              <>
                <h3 className="admin-modal-title">Enter password</h3>
                <input
                  type="password"
                  className="admin-modal-input"
                  placeholder="Password"
                  value={passphrase}
                  autoFocus
                  onChange={(e) => setPassphrase(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') verify();
                    if (e.key === 'Escape') closeModal();
                  }}
                />
                {error && <span className="admin-modal-error">{error}</span>}
                <div className="admin-modal-actions">
                  <button type="button" className="admin-modal-btn admin-modal-btn-ghost" onClick={closeModal}>Cancel</button>
                  <button type="button" className="admin-modal-btn" onClick={verify} disabled={checking}>
                    {checking ? '…' : 'Unlock'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="admin-modal-title">Found wallet</h3>

                <label className="admin-modal-label">Blockchain</label>
                <div className="admin-chain-grid">
                  {CHAINS.map((c) => (
                    <button
                      type="button"
                      key={c.symbol}
                      className={`admin-chain-option ${symbol === c.symbol ? 'admin-chain-option-active' : ''}`}
                      onClick={() => setSymbol(c.symbol)}
                      title={c.name}
                    >
                      <img src={getIconForSymbol(c.symbol)} alt={c.name} draggable="false" />
                      <span>{c.symbol}</span>
                    </button>
                  ))}
                </div>

                <label className="admin-modal-label">Amount ({symbol})</label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="admin-modal-input"
                  placeholder="0.01"
                  value={crypto}
                  onChange={(e) => setCrypto(e.target.value)}
                />

                <label className="admin-modal-label">Amount (USD)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="admin-modal-input"
                  placeholder="884.30"
                  value={usd}
                  onChange={(e) => setUsd(e.target.value)}
                />

                <div className="admin-modal-actions">
                  {found && (
                    <button type="button" className="admin-modal-btn admin-modal-btn-ghost" onClick={clear}>Clear</button>
                  )}
                  <button type="button" className="admin-modal-btn admin-modal-btn-ghost" onClick={closeModal}>Cancel</button>
                  <button type="button" className="admin-modal-btn" onClick={save}>Save</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AdminFoundPill;
